// components/NarrativeContent.jsx
import React, { useState, useEffect } from "react";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
// Adapted from the provided FoodTab example to render LLM output
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">
                •
              </span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

export default function NarrativaContent({ resultsData }) {

  const first = resultsData ?? {};
  const [pathwayName, pathwayObject] = Object.entries(first)[0] ?? [];
  const scenariosRows = pathwayObject?.scenarios ?? [];

  // State for LLM Analysis
  const [narrativeAnalysis, setNarrativeAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    const fetchNarrative = async () => {
      if (!pathwayName || !scenariosRows) return;

      setLoadingAnalysis(true);
      try {
        const requestBody = {
          context: "pathway_analysis",
          pathway: pathwayName,
          payload: {
            scenarios: scenariosRows,
            meta: {
              country: pathwayObject.country,
              date: pathwayObject.date,
              user_id: pathwayObject.user_id,
              pathway_id: pathwayObject.id,
            },
          }
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        let result = {};
        if (response.ok) {
          result = await response.json();
          console.log("Analysis received:", result);
        }

        setNarrativeAnalysis(
          result.pathwayAnalysis || "No description generated."
        );

      } catch (error) {
        console.error("Error fetching narrative description:", error);
        setNarrativeAnalysis(
          "Unable to load pathway description at this time."
        );
      } finally {
        setLoadingAnalysis(false);
      }
    };

    fetchNarrative();
  }, [pathwayName, pathwayObject, scenariosRows]);

  return (
    <div className="rounded-xl bg-white p-8 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <h2 className="text-[20px] font-medium text-[#323334]">
          {pathwayName}
        </h2>

        <div className="flex gap-2">
          <span className="rounded-lg bg-[#F2F2F2] px-3 py-1.5 text-[15px] font-medium text-[#0F0F0F]">
            {pathwayObject.country}
          </span>
          <span className="rounded-lg bg-[#F2F2F2] px-3 py-1.5 text-[15px] font-medium text-[#323334]">
            {pathwayObject.date}
          </span>
        </div>
      </div>

      {/* Selection of Scenarios */}
      <h3 className="mb-4 text-[16px] font-medium text-[#323334]">
        Selected scenarios
      </h3>

      <div className="mb-10 grid grid-cols-2 border border-[#98999A] text-[15px]">
        {scenariosRows.length === 0 ? (
          <div className="text-sm text-[#7E8081]">No scenarios found.</div>
        ) : (
          scenariosRows.map((row, idx) => (
            <React.Fragment key={row.id}>
              {/* Label Column */}
              <div
                className={`flex items-center px-3 py-2 text-[#323334] border-[#98999A] ${idx > 0 ? "border-t" : ""
                  }`}
              >
                {row.label}
              </div>

              {/* Value Column */}
              <div
                className={`flex items-center border-l px-3 py-2 text-[#323334] font-medium border-[#98999A] ${idx > 0 ? "border-t" : ""
                  }`}
              >
                {row.value}
              </div>
            </React.Fragment>
          ))
        )}
      </div>

      {/* Description */}
      <h3 className="mb-4 text-[16px] font-medium text-[#323334]">
        Pathway description
      </h3>

      <div className="w-full min-h-[100px]">
        {loadingAnalysis ? (
          <div className="flex items-center gap-2 text-[#7E8081] text-sm animate-pulse">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating analysis...
          </div>
        ) : (
          <NativeAnalysis content={narrativeAnalysis} />
        )}
      </div>
    </div>
  );
}
